import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, ZoomControl, useMap } from 'react-leaflet';
import { doc, updateDoc, collection, query, where, getDocs, serverTimestamp } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebaseConfig';
import { useSkipsData } from '../context/SkipsDataContext';
import { useBatchedUpdates } from '../context/BatchedUpdatesContext';
import { useResponsive } from '../hooks/useResponsive';
import toast from 'react-hot-toast';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import './MapComponent.css';
import L from 'leaflet';
import MarkerClusterGroup from 'react-leaflet-markercluster';
import html2canvas from 'html2canvas';
import SearchBar from './SearchBar';
import ViewToggle from './ViewToggle';
import FilterSidebar from './FilterSidebar';
import MobileCapabilityWarning from './MobileCapabilityWarning';
import { parseJobDate } from '../utils/dateUtils';

// Fix for default marker icons in Leaflet
delete L.Icon.Default.prototype._getIconUrl;

const createCustomIcon = (color, opacity = 1, isAssigned = false) => {
    const personIcon = isAssigned ? `
        <div style="
            position: absolute;
            top: -6px;
            right: -6px;
            width: 14px;
            height: 14px;
            background-color: #166534;
            border-radius: 50%;
            border: 1.5px solid white;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 8px;
            color: white;
            box-shadow: 0 1px 3px rgba(28, 25, 23, 0.2);
        ">👤</div>
    ` : '';

    return L.divIcon({
        className: 'custom-icon',
        html: `<div style="
            position: relative;
            background-color: ${color};
            width: 24px;
            height: 24px;
            border-radius: 50%;
            border: 2px solid white;
            box-shadow: 0 2px 6px rgba(28, 25, 23, 0.25);
            opacity: ${opacity};"
        >${personIcon}</div>`,
        iconSize: [24, 24],
        iconAnchor: [12, 12],
        popupAnchor: [0, -12],
    });
};

// Icon cache to prevent recreating identical Leaflet icons
const iconCache = new Map();

const getCachedIcon = (color, opacity, isAssigned) => {
    // Round opacity to 1 decimal place to improve cache hits
    const roundedOpacity = Math.round(opacity * 10) / 10;
    const key = `${color}-${roundedOpacity}-${isAssigned}`;

    if (!iconCache.has(key)) {
        iconCache.set(key, createCustomIcon(color, roundedOpacity, isAssigned));
    }
    return iconCache.get(key);
};

// Cluster icon cache
const clusterIconCache = new Map();

const getCachedClusterIcon = (color, count) => {
    const key = `${color}-${count}`;

    if (!clusterIconCache.has(key)) {
        clusterIconCache.set(key, L.divIcon({
            html: `<div style="
                background-color: ${color};
                width: 38px;
                height: 38px;
                border-radius: 50%;
                border: 2.5px solid white;
                box-shadow: 0 2px 8px rgba(28, 25, 23, 0.25);
                display: flex;
                align-items: center;
                justify-content: center;
                color: white;
                font-weight: 600;
                font-size: 13px;
                font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
            ">${count}</div>`,
            className: 'custom-cluster-icon',
            iconSize: L.point(38, 38, true),
        }));
    }
    return clusterIconCache.get(key);
};

// Component to auto-center map on initial load when markers are ready
function MapAutoCenter({ markers, hasInitiallyCentered, setHasInitiallyCentered }) {
    const map = useMap();

    useEffect(() => {
        // Only auto-center if:
        // 1. We haven't centered yet
        // 2. We have markers to center on
        // 3. There's no focus job from records (let MapFocusHandler handle that case)
        const focusJobId = sessionStorage.getItem('focusJobId');

        if (!hasInitiallyCentered && markers.length > 0 && !focusJobId) {
            try {
                // Calculate center of all markers
                const sumLat = markers.reduce((sum, marker) => sum + (marker.position?.[0] || 0), 0);
                const sumLng = markers.reduce((sum, marker) => sum + (marker.position?.[1] || 0), 0);
                const center = [sumLat / markers.length, sumLng / markers.length];

                // Set view to calculated center
                map.setView(center, 12, {
                    animate: true,
                    duration: 1.0
                });

                // Mark that we've centered
                setHasInitiallyCentered(true);
            } catch (error) {
                console.error('Error auto-centering map:', error);
            }
        }
    }, [map, markers, hasInitiallyCentered, setHasInitiallyCentered]);

    return null;
}

// Component to handle map focusing when navigating from records
function MapFocusHandler({ markers, markerRefs }) {
    const map = useMap();

    useEffect(() => {
        const focusJobId = sessionStorage.getItem('focusJobId');
        const mapCenterData = sessionStorage.getItem('mapCenter');

        if (focusJobId && mapCenterData) {
            try {
                const { lat, lng, zoom } = JSON.parse(mapCenterData);

                // Center and zoom map
                map.setView([lat, lng], zoom, {
                    animate: true,
                    duration: 1.0
                });

                // Find and open the popup for the focused marker
                // Increased timeout to allow map animation to complete (1000ms) and markers to uncluster
                setTimeout(() => {
                    const markerToOpen = markerRefs.current[focusJobId];
                    if (markerToOpen) {
                        markerToOpen.openPopup();
                    }
                    toast.success('Job location displayed on map');

                    // Also highlight marker visually
                    const markerElements = document.querySelectorAll('.custom-icon');
                    markerElements.forEach((markerEl) => {
                        const parent = markerEl.closest('.leaflet-marker-icon');
                        if (parent && parent.dataset.markerId === focusJobId) {
                            parent.classList.add('marker-highlighted');
                            setTimeout(() => {
                                parent.classList.remove('marker-highlighted');
                            }, 3000);
                        }
                    });
                }, 1200);
            } catch (error) {
                console.error('Error focusing on job:', error);
                toast.error('Failed to focus on job location');
            } finally {
                sessionStorage.removeItem('focusJobId');
                sessionStorage.removeItem('mapCenter');
            }
        }
    }, [map, markers, markerRefs]);

    return null;
}

const MapComponent = () => {
    const navigate = useNavigate();
    const auth = getAuth();
    const viewport = useResponsive();
    const currentTime = new Date();

    const {
        companyId,
        companyName,
        userIsAdmin,
        isLoading,
        error,
        jobs,
        getFilteredJobsForMap,
        getMarkerColor
    } = useSkipsData();

    // Use batched updates for optimistic collection marking
    const { markAsCollected } = useBatchedUpdates();

    const [isOpen, setIsOpen] = useState(() => window.innerWidth >= 768);
    const [enlargedImage, setEnlargedImage] = useState(null);
    const [mapInstance, setMapInstance] = useState(null);
    const [optimisticallyCollected, setOptimisticallyCollected] = useState(new Set());
    const [searchMatchedMarkers, setSearchMatchedMarkers] = useState([]);
    const [showPrintPreview, setShowPrintPreview] = useState(false);
    const [printOptions, setPrintOptions] = useState({
        includeLegend: true,
        includeJobList: false
    });
    const [showMobileWarning, setShowMobileWarning] = useState(false);
    const [selectedMobileMarker, setSelectedMobileMarker] = useState(null);
    const [hasInitiallyCentered, setHasInitiallyCentered] = useState(false);
    const [drivers, setDrivers] = useState([]);
    const [assigningJobs, setAssigningJobs] = useState(new Set());
    const [mapLayer, setMapLayer] = useState('street'); // 'street' | 'voyager' | 'toner'
    const mapContainerRef = useRef(null);
    const markerRefs = useRef({});

    // Map tile layer configurations
    const tileLayers = {
        street: {
            url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        },
        voyager: {
            url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
        },
        toner: {
            url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
        },
        satellite: {
            url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
            attribution: '&copy; <a href="https://www.esri.com/">Esri</a>, Maxar, Earthstar Geographics'
        },
        dark: {
            url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
        }
    };

    // Fetch drivers for the company (for assignment dropdown)
    useEffect(() => {
        const fetchDrivers = async () => {
            if (!companyId) return;

            try {
                const usersRef = collection(db, 'users');
                const q = query(
                    usersRef,
                    where('companyId', '==', companyId)
                );
                const snapshot = await getDocs(q);

                const driversList = [];
                snapshot.forEach((doc) => {
                    const data = doc.data();
                    // Include all users (both drivers and admins can be assigned)
                    driversList.push({
                        id: doc.id,
                        name: data.name || data.email?.split('@')[0] || 'Unknown',
                        email: data.email,
                        isAdmin: data.admin === true
                    });
                });

                // Sort by name
                driversList.sort((a, b) => a.name.localeCompare(b.name));
                setDrivers(driversList);
            } catch (error) {
                console.error('Error fetching drivers:', error);
            }
        };

        fetchDrivers();
    }, [companyId]);

    // Handle driver assignment
    const handleAssignDriver = async (jobId, driverId, driverName, assignmentType, mapInstance) => {
        setAssigningJobs(prev => new Set(prev).add(jobId));

        try {
            const docRef = doc(db, 'jobs', jobId);
            await updateDoc(docRef, {
                assignedDriverId: driverId || null,
                assignedDriverName: driverName || null,
                assignmentType: driverId ? assignmentType : null,
                assignedAt: driverId ? serverTimestamp() : null,
                assignedBy: driverId ? auth.currentUser?.uid : null
            });

            if (driverId) {
                toast.success(`Assigned ${assignmentType} to ${driverName}`);
            } else {
                toast.success('Assignment removed');
            }

            // Close popup after successful update
            if (mapInstance) {
                mapInstance.closePopup();
            }
        } catch (error) {
            console.error('Error assigning driver:', error);
            toast.error('Failed to assign driver');
        } finally {
            setAssigningJobs(prev => {
                const next = new Set(prev);
                next.delete(jobId);
                return next;
            });
        }
    };


    // Show mobile capability warning once per session
    useEffect(() => {
        if (viewport.isMobile && !sessionStorage.getItem('mobileWarningDismissed')) {
            setShowMobileWarning(true);
        }
    }, [viewport.isMobile]);

    const filteredMarkers = getFilteredJobsForMap();

    // Total delivered jobs (unfiltered baseline for map view)
    const totalJobs = useMemo(() => {
        return jobs.filter(job => job.status?.toLowerCase() === 'delivered').length;
    }, [jobs]);

    // Filter out optimistically collected markers (instant removal from map)
    // Use useMemo to ensure proper re-rendering when optimisticallyCollected changes
    const activeSkips = useMemo(() => {
        return filteredMarkers.filter(marker => !optimisticallyCollected.has(marker.id));
    }, [filteredMarkers, optimisticallyCollected]);

    // Custom cluster icon function - colors cluster based on most overdue marker
    // Memoized with useCallback to prevent recreating on every render
    const createClusterCustomIcon = useCallback((cluster) => {
        const childMarkers = cluster.getAllChildMarkers();
        const childCount = childMarkers.length;

        // Determine the "worst case" color from all child markers
        let hasRed = false;
        let hasYellow = false;

        for (const marker of childMarkers) {
            const initialTime = marker.options.initialTime;
            const timestamp = marker.options.timestamp;
            if (initialTime || timestamp) {
                const color = getMarkerColor(initialTime, timestamp);
                if (color === '#F44336') {
                    hasRed = true;
                    break; // Red is highest priority, no need to continue
                } else if (color === '#FFC107') {
                    hasYellow = true;
                }
            }
        }

        // Priority: Red > Yellow > Green
        const clusterColor = hasRed ? '#F44336' : hasYellow ? '#FFC107' : '#4CAF50';

        // Use cached cluster icon
        return getCachedClusterIcon(clusterColor, childCount);
    }, [getMarkerColor]);

    const toggleEnlargedImage = (imageUrl) => {
        if (enlargedImage === imageUrl) {
            setEnlargedImage(null);
        } else {
            setEnlargedImage(imageUrl);
        }
    };

    // Handle search results for marker dimming
    const handleSearchResults = (results) => {
        setSearchMatchedMarkers(results);
    };

    // Capture map as image
    const captureMap = async () => {
        if (!mapContainerRef.current) {
            toast.error('Map not ready for capture');
            return null;
        }

        try {
            const mapElement = mapContainerRef.current.querySelector('.leaflet-container');
            const canvas = await html2canvas(mapElement, {
                useCORS: true,
                allowTaint: true,
                backgroundColor: '#ffffff'
            });
            return canvas.toDataURL('image/png');
        } catch (error) {
            console.error('Error capturing map:', error);
            toast.error('Failed to capture map');
            return null;
        }
    };

    // Generate print document
    const handlePrint = async () => {
        toast.loading('Preparing map for printing...', { id: 'print' });

        const mapImage = await captureMap();
        if (!mapImage) {
            toast.error('Failed to capture map', { id: 'print' });
            return;
        }

        // Generate legend HTML
        const legendHTML = printOptions.includeLegend ? `
            <div style="margin: 20px 0; padding: 15px; background: #f9f9f9; border-radius: 8px;">
                <h3 style="margin-top: 0;">Legend</h3>
                <div style="display: flex; gap: 20px; flex-wrap: wrap;">
                    <div style="display: flex; align-items: center; gap: 8px;">
                        <div style="width: 20px; height: 20px; background: #4CAF50; border-radius: 50%; border: 2px solid white;"></div>
                        <span>0-7 days</span>
                    </div>
                    <div style="display: flex; align-items: center; gap: 8px;">
                        <div style="width: 20px; height: 20px; background: #FFC107; border-radius: 50%; border: 2px solid white;"></div>
                        <span>7-14 days</span>
                    </div>
                    <div style="display: flex; align-items: center; gap: 8px;">
                        <div style="width: 20px; height: 20px; background: #F44336; border-radius: 50%; border: 2px solid white;"></div>
                        <span>14+ days</span>
                    </div>
                </div>
            </div>
        ` : '';

        // Generate job list HTML
        const jobListHTML = printOptions.includeJobList ? `
            <div style="margin: 20px 0;">
                <h3>Active Skips (${activeSkips.length})</h3>
                <table style="width: 100%; border-collapse: collapse; font-size: 12px;">
                    <thead>
                        <tr style="background: #f0f0f0;">
                            <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Address</th>
                            <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Skip Size</th>
                            <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Delivery Date</th>
                            <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Days On Site</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${activeSkips.map(marker => `
                            <tr>
                                <td style="border: 1px solid #ddd; padding: 8px;">${marker.streetAddress || 'N/A'}</td>
                                <td style="border: 1px solid #ddd; padding: 8px;">${marker.skipSize || 'N/A'}</td>
                                <td style="border: 1px solid #ddd; padding: 8px;">${marker.deliveryDate || 'N/A'}</td>
                                <td style="border: 1px solid #ddd; padding: 8px;">${(() => { const d = parseJobDate(marker.initialTime, marker.timestamp); return d ? Math.round((Date.now() - d) / (1000 * 60 * 60 * 24)) : 'N/A'; })()}</td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
            </div>
        ` : '';

        // Open print window
        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>Skip Delivery Map - ${companyId}</title>
                <style>
                    @page {
                        size: A4 landscape;
                        margin: 15mm;
                    }
                    body {
                        font-family: Arial, sans-serif;
                        margin: 0;
                        padding: 20px;
                    }
                    h1 {
                        margin-top: 0;
                        color: #333;
                    }
                    img {
                        max-width: 100%;
                        height: auto;
                        border: 1px solid #ddd;
                        border-radius: 8px;
                        margin: 20px 0;
                    }
                    @media print {
                        body {
                            padding: 0;
                        }
                    }
                </style>
            </head>
            <body>
                <h1>Skip Delivery Map</h1>
                <p><strong>Company:</strong> ${companyId}</p>
                <p><strong>Date:</strong> ${new Date().toLocaleDateString()}</p>
                <p><strong>Active Skips:</strong> ${activeSkips.length}</p>

                <img src="${mapImage}" alt="Skip Delivery Map" />

                ${legendHTML}
                ${jobListHTML}

                <script>
                    window.onload = function() {
                        window.print();
                    };
                </script>
            </body>
            </html>
        `);
        printWindow.document.close();

        toast.success('Print dialog opened', { id: 'print' });
        setShowPrintPreview(false);
    };

    const handleClearSkip = (markerId, skipAddress) => {
        // Close popup through marker ref
        const markerRef = markerRefs.current[markerId];
        if (markerRef) {
            markerRef.closePopup();
        }

        // Add to optimistically collected set (instant visual removal)
        setOptimisticallyCollected(prev => new Set(prev).add(markerId));

        // Queue the update to be batched and saved (250ms debounce)
        markAsCollected(markerId, auth.currentUser?.uid || 'unknown');

        // Subtle success toast
        toast.success('Marked as collected ✓', {
            duration: 1000,
            position: 'bottom-right',
            style: {
                background: '#10B981',
                color: 'white',
                fontSize: '14px',
                padding: '8px 16px',
                borderRadius: '6px'
            }
        });
    };

    const handleViewDetails = (jobId) => {
        sessionStorage.setItem('focusRowId', jobId);
        toast.success('Opening records view...');
        navigate('/records');
    };

    const markersJson = JSON.stringify(filteredMarkers);

    useEffect(() => {
        const markers = JSON.parse(markersJson);
        if (markers.length > 0 && mapInstance) {
            const sumLat = markers.reduce((sum, marker) => {
                if (!marker.position || !marker.position[0]) return sum;
                return sum + parseFloat(marker.position[0]);
            }, 0);

            const sumLng = markers.reduce((sum, marker) => {
                if (!marker.position || !marker.position[1]) return sum;
                return sum + parseFloat(marker.position[1]);
            }, 0);

            const center = [
                sumLat / markers.length,
                sumLng / markers.length
            ];

            mapInstance.flyTo(center, 12);
        }
    }, [markersJson, mapInstance]);

    useEffect(() => {
        if (mapInstance && enlargedImage) {
            const closeEnlargedImage = () => {
                setEnlargedImage(null);
            };

            mapInstance.on('click', closeEnlargedImage);

            return () => {
                mapInstance.off('click', closeEnlargedImage);
            };
        }
    }, [mapInstance, enlargedImage]);

    useEffect(() => {
        if (mapInstance) {
            const handlePopupOpen = () => {
                setEnlargedImage(null);
            };

            mapInstance.on('popupopen', handlePopupOpen);

            return () => {
                mapInstance.off('popupopen', handlePopupOpen);
            };
        }
    }, [mapInstance]);

    const getMapCenter = () => {
        if (activeSkips.length === 0) {
            return [51.505, -0.09];
        }

        try {
            const sumLat = activeSkips.reduce((sum, marker) => {
                if (!marker.position || !marker.position[0]) return sum;
                return sum + parseFloat(marker.position[0]);
            }, 0);

            const sumLng = activeSkips.reduce((sum, marker) => {
                if (!marker.position || !marker.position[1]) return sum;
                return sum + parseFloat(marker.position[1]);
            }, 0);

            const center = [
                sumLat / activeSkips.length,
                sumLng / activeSkips.length
            ];

            return center;
        } catch (err) {
            console.error("Error calculating map center:", err);
            return [51.505, -0.09];
        }
    };

    

    if (isLoading) return (
        <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh'
        }}>
            Loading markers for {companyName || companyId}...
        </div>
    );

    if (error) return (
        <div style={{
            color: 'red',
            textAlign: 'center',
            marginTop: '20px'
        }}>
            Error loading markers: {error.message}
        </div>
    );

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100dvh', width: '100vw' }}>
            {/* Fixed Header with View Toggle */}
            <ViewToggle />

            {/* Main Content Area */}
            <div style={{ display: 'flex', flex: 1, marginTop: '50px', position: 'relative' }}>
                {/* Filter Sidebar */}
                <FilterSidebar view="map" isOpen={isOpen} onToggle={() => setIsOpen(!isOpen)} />

                {/* Debug info display */}
                <div style={{
                    position: 'absolute',
                    right: '20px',
                    bottom: '20px',
                    zIndex: 1001,
                    padding: '12px 16px',
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    borderRadius: '4px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    fontSize: '12px',
                    maxWidth: '300px'
                }}>
                    <p style={{ margin: 0, fontWeight: '500' }}>{activeSkips.length} selected · {totalJobs} total</p>
                </div>

                {/* Print Map Button - Desktop Only */}
                {!viewport.isMobile && (
                    <button
                        onClick={() => setShowPrintPreview(true)}
                        style={{
                            position: 'absolute',
                            top: '10px',
                            right: 'max(70px, 5vw)',
                            zIndex: 1001,
                            padding: '8px 16px',
                            backgroundColor: '#166534',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            boxShadow: '0 1px 3px rgba(28, 25, 23, 0.06)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#14532D'}
                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#166534'}
                    >
                        Print Map
                    </button>
                )}

                {/* Map Container */}
                <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }}>
                    <MapContainer
                        center={getMapCenter()}
                        zoom={12}
                        style={{ width: '100%', height: '100%' }}
                        zoomControl={false}
                        whenCreated={setMapInstance}
                        zoomSnap={0.5}
                        zoomDelta={0.5}
                        wheelPxPerZoomLevel={120}
                        zoomAnimation={true}
                        fadeAnimation={true}
                        markerZoomAnimation={true}
                        inertia={true}
                        inertiaDeceleration={2000}
                        inertiaMaxSpeed={1500}
                        easeLinearity={0.25}
                    >
                        <TileLayer
                            key={mapLayer}
                            url={tileLayers[mapLayer].url}
                            attribution={tileLayers[mapLayer].attribution}
                        />
                        <ZoomControl position="topright" />

                        <MapAutoCenter
                            markers={filteredMarkers}
                            hasInitiallyCentered={hasInitiallyCentered}
                            setHasInitiallyCentered={setHasInitiallyCentered}
                        />
                        <MapFocusHandler markers={filteredMarkers} markerRefs={markerRefs} />

                        <SearchBar markers={filteredMarkers} onSearchResults={handleSearchResults} isMobile={viewport.isMobile} />

                        <MarkerClusterGroup
                            disableClusteringAtZoom={19}
                            maxClusterRadius={40}
                            spiderfyOnMaxZoom={true}
                            spiderfyDistanceMultiplier={2}
                            showCoverageOnHover={false}
                            zoomToBoundsOnClick={true}
                            iconCreateFunction={createClusterCustomIcon}
                        >
                        {activeSkips.map((marker) => {
                            if (!marker.position || !Array.isArray(marker.position) ||
                                marker.position.length !== 2 ||
                                marker.position[0] == null || isNaN(marker.position[0]) ||
                                marker.position[1] == null || isNaN(marker.position[1])) {
                                console.error("Invalid marker position, skipping:", marker);
                                return null;
                            }

                            const color = getMarkerColor(marker.initialTime, marker.timestamp);

                            // Calculate opacity based on search results
                            const opacity = searchMatchedMarkers.length > 0
                                ? (searchMatchedMarkers.some(m => m.id === marker.id) ? 1 : 0.3)
                                : 1;

                            // Check if job is assigned
                            const isAssigned = !!marker.assignedDriverId;

                            // Use cached icon to prevent recreating on every render
                            const icon = getCachedIcon(color, opacity, isAssigned);

                            return (
                                <Marker
                                    key={marker.id}
                                    ref={(el) => (markerRefs.current[marker.id] = el)}
                                    position={marker.position}
                                    icon={icon}
                                    initialTime={marker.initialTime}
                                    timestamp={marker.timestamp}
                                    eventHandlers={{
                                        click: () => {
                                            setEnlargedImage(null);
                                            // On mobile, show bottom sheet with marker details
                                            if (viewport.isMobile) {
                                                setSelectedMobileMarker(marker);
                                            }
                                        },
                                        add: (e) => {
                                            // Add data-marker-id attribute for focus handling
                                            const markerElement = e.target._icon;
                                            if (markerElement) {
                                                markerElement.dataset.markerId = marker.id;
                                            }
                                        }
                                    }}
                                >
                                    {/* Popup only shown on desktop - hidden on mobile to avoid obscuring view */}
                                    {!viewport.isMobile && (
                                        <Popup
                                            onOpen={() => {
                                                setEnlargedImage(null);
                                            }}
                                            closeButton={false}
                                        >
                                        <div style={{ maxWidth: '280px', position: 'relative' }}>
                                            {marker.imageUrl && (
                                                <div style={{
                                                    width: '240px',
                                                    margin: '0 auto 12px auto',
                                                    borderRadius: '4px',
                                                    overflow: 'hidden',
                                                    boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                                                    position: 'relative'
                                                }}>
                                                    <img
                                                        src={marker.imageUrl}
                                                        alt="Skip location"
                                                        loading="lazy"
                                                        style={{
                                                            width: '100%',
                                                            height: '160px',
                                                            objectFit: 'cover',
                                                            cursor: 'pointer'
                                                        }}
                                                        onClick={() => toggleEnlargedImage(marker.imageUrl)}
                                                        onError={(e) => {
                                                            console.error("Error loading image:", e);
                                                            e.target.src = "https://via.placeholder.com/300x200?text=Image+Unavailable";
                                                            e.target.style.opacity = 0.6;
                                                        }}
                                                    />
                                                </div>
                                            )}

                                            <div style={{ fontSize: '14px' }}>
                                                <p style={{
                                                    margin: '8px 0',
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    borderBottom: '1px solid #f0f0f0',
                                                    paddingBottom: '6px'
                                                }}>
                                                    <span style={{ color: '#666', fontWeight: '500' }}>Hire Days:</span>
                                                    <span style={{ fontWeight: '600' }}>
                                                        {(() => { const d = parseJobDate(marker.initialTime, marker.timestamp); return d ? Math.round((currentTime - d) / (1000 * 60 * 60 * 24)) : 'N/A'; })()} days
                                                    </span>
                                                </p>

                                                <p style={{
                                                    margin: '8px 0',
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    borderBottom: '1px solid #f0f0f0',
                                                    paddingBottom: '6px'
                                                }}>
                                                    <span style={{ color: '#666', fontWeight: '500' }}>Payment Status:</span>
                                                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                        <span style={{
                                                            fontWeight: '600',
                                                            color: marker.paymentStatus === 'TO PAY' ? '#EF4444' : '#000'
                                                        }}>
                                                            {marker.paymentStatus || 'Unknown'}
                                                        </span>
                                                        {marker.invoiced && (
                                                            <span style={{
                                                                padding: '1px 4px',
                                                                borderRadius: '3px',
                                                                fontSize: '9px',
                                                                fontWeight: '600',
                                                                backgroundColor: '#DBEAFE',
                                                                color: '#1E40AF'
                                                            }}>
                                                                INV
                                                            </span>
                                                        )}
                                                    </span>
                                                </p>

                                                <p style={{
                                                    margin: '8px 0',
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    borderBottom: '1px solid #f0f0f0',
                                                    paddingBottom: '6px'
                                                }}>
                                                    <span style={{ color: '#666', fontWeight: '500' }}>Skip Size:</span>
                                                    <span style={{ fontWeight: '600' }}>{marker.size}</span>
                                                </p>

                                                <p style={{
                                                    margin: '8px 0',
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    borderBottom: '1px solid #f0f0f0',
                                                    paddingBottom: '6px'
                                                }}>
                                                    <span style={{ color: '#666', fontWeight: '500' }}>Delivery Driver:</span>
                                                    <span style={{ fontWeight: '600' }}>{marker.driverName}</span>
                                                </p>

                                                <p style={{
                                                    margin: '8px 0',
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    borderBottom: '1px solid #f0f0f0',
                                                    paddingBottom: '6px'
                                                }}>
                                                    <span style={{ color: '#666', fontWeight: '500' }}>Waste Type:</span>
                                                    <span style={{ fontWeight: '600' }}>{marker.description}</span>
                                                </p>

                                                <p style={{
                                                    margin: '8px 0',
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    borderBottom: '1px solid #f0f0f0',
                                                    paddingBottom: '6px'
                                                }}>
                                                    <span style={{ color: '#666', fontWeight: '500' }}>Address:</span>
                                                    <span style={{ fontWeight: '600', maxWidth: '60%', textAlign: 'right' }}>
                                                        {marker.streetAddress ? marker.streetAddress : "No address provided"}
                                                    </span>
                                                </p>

                                                {/* Note */}
                                                {marker.driverNote && (
                                                    <div style={{
                                                        margin: '8px 0',
                                                        padding: '8px',
                                                        backgroundColor: '#f9fafb',
                                                        borderRadius: '4px',
                                                        border: '1px solid #e5e7eb'
                                                    }}>
                                                        <div style={{
                                                            color: '#666',
                                                            fontWeight: '500',
                                                            fontSize: '12px',
                                                            marginBottom: '4px',
                                                            textTransform: 'uppercase',
                                                            letterSpacing: '0.5px'
                                                        }}>
                                                            Note
                                                        </div>
                                                        <div style={{
                                                            color: '#374151',
                                                            fontSize: '13px',
                                                            lineHeight: '1.4',
                                                            whiteSpace: 'pre-wrap',
                                                            wordBreak: 'break-word'
                                                        }}>
                                                            {marker.driverNote}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Driver Assignment Dropdowns - Admin only */}
                                                {userIsAdmin && (
                                                    <div style={{
                                                        margin: '8px 0',
                                                        paddingTop: '8px',
                                                        borderTop: '1px solid #f0f0f0'
                                                    }}>
                                                        {/* Collection Assignment */}
                                                        <div style={{ marginBottom: '12px' }}>
                                                            <label style={{
                                                                display: 'block',
                                                                color: '#666',
                                                                fontWeight: '500',
                                                                marginBottom: '6px',
                                                                fontSize: '13px'
                                                            }}>
                                                                Assign Collection To:
                                                            </label>
                                                            <select
                                                                value={marker.assignmentType === 'Collection' ? (marker.assignedDriverId || '') : ''}
                                                                onChange={(e) => {
                                                                    const selectedId = e.target.value;
                                                                    const selectedDriver = drivers.find(d => d.id === selectedId);
                                                                    handleAssignDriver(
                                                                        marker.id,
                                                                        selectedId || null,
                                                                        selectedDriver?.name || null,
                                                                        'Collection',
                                                                        mapInstance
                                                                    );
                                                                }}
                                                                disabled={assigningJobs.has(marker.id)}
                                                                style={{
                                                                    width: '100%',
                                                                    padding: '8px 10px',
                                                                    borderRadius: '6px',
                                                                    border: '1px solid #d1d5db',
                                                                    backgroundColor: assigningJobs.has(marker.id) ? '#f3f4f6' : 'white',
                                                                    fontSize: '13px',
                                                                    cursor: assigningJobs.has(marker.id) ? 'not-allowed' : 'pointer',
                                                                    color: (marker.assignmentType === 'Collection' && marker.assignedDriverId) ? '#111827' : '#6b7280'
                                                                }}
                                                            >
                                                                <option value="">None (Unassigned)</option>
                                                                {drivers.map((driver) => (
                                                                    <option key={driver.id} value={driver.id}>
                                                                        {driver.name}{driver.isAdmin ? ' (Admin)' : ''}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </div>

                                                        {/* Exchange Assignment */}
                                                        <div>
                                                            <label style={{
                                                                display: 'block',
                                                                color: '#666',
                                                                fontWeight: '500',
                                                                marginBottom: '6px',
                                                                fontSize: '13px'
                                                            }}>
                                                                Assign Exchange To:
                                                            </label>
                                                            <select
                                                                value={marker.assignmentType === 'Exchange' ? (marker.assignedDriverId || '') : ''}
                                                                onChange={(e) => {
                                                                    const selectedId = e.target.value;
                                                                    const selectedDriver = drivers.find(d => d.id === selectedId);
                                                                    handleAssignDriver(
                                                                        marker.id,
                                                                        selectedId || null,
                                                                        selectedDriver?.name || null,
                                                                        'Exchange',
                                                                        mapInstance
                                                                    );
                                                                }}
                                                                disabled={assigningJobs.has(marker.id)}
                                                                style={{
                                                                    width: '100%',
                                                                    padding: '8px 10px',
                                                                    borderRadius: '6px',
                                                                    border: '1px solid #d1d5db',
                                                                    backgroundColor: assigningJobs.has(marker.id) ? '#f3f4f6' : 'white',
                                                                    fontSize: '13px',
                                                                    cursor: assigningJobs.has(marker.id) ? 'not-allowed' : 'pointer',
                                                                    color: (marker.assignmentType === 'Exchange' && marker.assignedDriverId) ? '#111827' : '#6b7280'
                                                                }}
                                                            >
                                                                <option value="">None (Unassigned)</option>
                                                                {drivers.map((driver) => (
                                                                    <option key={driver.id} value={driver.id}>
                                                                        {driver.name}{driver.isAdmin ? ' (Admin)' : ''}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                        {marker.assignedDriverId && marker.assignedAt && (
                                                            <p style={{
                                                                fontSize: '11px',
                                                                color: '#9ca3af',
                                                                marginTop: '4px',
                                                                marginBottom: '0'
                                                            }}>
                                                                Assigned {new Date(marker.assignedAt?.toDate?.() || marker.assignedAt).toLocaleDateString()}
                                                            </p>
                                                        )}

                                                    </div>
                                                )}
                                            </div>

                                            {/* View Details button */}
                                            <div style={{
                                                marginTop: '16px',
                                                paddingTop: '12px',
                                                borderTop: '2px solid #f0f0f0'
                                            }}>
                                                <button
                                                    onClick={() => handleViewDetails(marker.id)}
                                                    style={{
                                                        width: '100%',
                                                        padding: '8px 16px',
                                                        backgroundColor: '#166534',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '6px',
                                                        fontSize: '14px',
                                                        fontWeight: '500',
                                                        cursor: 'pointer',
                                                        transition: 'all 150ms ease',
                                                        marginBottom: userIsAdmin ? '8px' : '0'
                                                    }}
                                                    onMouseOver={(e) => {
                                                        e.currentTarget.style.backgroundColor = '#14532D';
                                                    }}
                                                    onMouseOut={(e) => {
                                                        e.currentTarget.style.backgroundColor = '#166534';
                                                    }}
                                                >
                                                    View Full Details
                                                </button>
                                            </div>

                                            {userIsAdmin && !viewport.isMobile && (
                                                <div style={{
                                                    marginTop: '0'
                                                }}>
                                                    <button
                                                        onClick={() => handleClearSkip(marker.id, marker.streetAddress)}
                                                        style={{
                                                            width: '100%',
                                                            padding: '10px 16px',
                                                            backgroundColor: '#DC2626',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '6px',
                                                            fontSize: '14px',
                                                            fontWeight: '600',
                                                            cursor: 'pointer',
                                                            transition: 'all 0.2s ease',
                                                            boxShadow: '0 2px 4px rgba(220, 38, 38, 0.3)'
                                                        }}
                                                        onMouseOver={(e) => {
                                                            e.currentTarget.style.backgroundColor = '#B91C1C';
                                                        }}
                                                        onMouseOut={(e) => {
                                                            e.currentTarget.style.backgroundColor = '#DC2626';
                                                        }}
                                                    >
                                                        Mark Skip as Collected
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                        </Popup>
                                    )}
                                </Marker>
                            );
                        })}
                        </MarkerClusterGroup>
                    </MapContainer>

                    {/* Layer toggle - top left, above sidebar (hidden on mobile) */}
                    {!viewport.isMobile && (
                    <div style={{
                        position: 'fixed',
                        top: '58px',
                        left: '5px',
                        zIndex: 1003,
                        display: 'flex',
                        gap: '4px',
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        padding: '4px',
                        borderRadius: '6px',
                        boxShadow: '0 2px 8px rgba(28, 25, 23, 0.12)',
                        border: '1px solid #E7E5E4'
                    }}>
                        {[
                            { key: 'street', label: 'Street' },
                            { key: 'dark', label: 'Dark' },
                            { key: 'toner', label: 'Light' },
                            { key: 'voyager', label: 'Clean' },
                            { key: 'satellite', label: 'Aerial' }
                        ].map(layer => (
                            <button
                                key={layer.key}
                                onClick={() => setMapLayer(layer.key)}
                                style={{
                                    padding: '5px 10px',
                                    fontSize: '11px',
                                    fontWeight: mapLayer === layer.key ? '600' : '400',
                                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                                    backgroundColor: mapLayer === layer.key ? '#166534' : 'transparent',
                                    color: mapLayer === layer.key ? 'white' : '#57534E',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    transition: 'all 0.15s ease'
                                }}
                                onMouseEnter={(e) => {
                                    if (mapLayer !== layer.key) {
                                        e.target.style.backgroundColor = '#F5F5F4';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (mapLayer !== layer.key) {
                                        e.target.style.backgroundColor = 'transparent';
                                    }
                                }}
                            >
                                {layer.label}
                            </button>
                        ))}
                    </div>
                    )}
                </div>
            </div>

            {/* Enlarged image overlay */}
            {enlargedImage && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2000,
                        cursor: 'pointer',
                        padding: '20px'
                    }}
                    onClick={() => setEnlargedImage(null)}
                >
                    <div
                        style={{
                            maxWidth: '85%',
                            maxHeight: '85%',
                            position: 'relative',
                            overflow: 'hidden'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <img
                            src={enlargedImage}
                            alt="Enlarged view"
                            style={{
                                maxWidth: '100%',
                                maxHeight: '80vh',
                                objectFit: 'contain',
                                borderRadius: '4px',
                                boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                            }}
                        />
                        <button
                            style={{
                                position: 'absolute',
                                top: '10px',
                                right: '10px',
                                width: '36px',
                                height: '36px',
                                borderRadius: '50%',
                                backgroundColor: 'white',
                                color: 'black',
                                border: 'none',
                                fontSize: '20px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                boxShadow: '0 2px 5px rgba(0,0,0,0.3)'
                            }}
                            onClick={() => setEnlargedImage(null)}
                        >
                            ×
                        </button>

                        <a
                            href={enlargedImage}
                            download="skip-image.jpg"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                position: 'absolute',
                                top: '10px',
                                left: '10px',
                                width: '36px',
                                height: '36px',
                                borderRadius: '50%',
                                backgroundColor: 'white',
                                color: 'black',
                                border: 'none',
                                fontSize: '18px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                boxShadow: '0 2px 5px rgba(0,0,0,0.3)',
                                textDecoration: 'none'
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            ⇩
                        </a>
                    </div>
                </div>
            )}

            {/* Print Preview Modal */}
            {showPrintPreview && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2000,
                        padding: '20px'
                    }}
                    onClick={() => setShowPrintPreview(false)}
                >
                    <div
                        style={{
                            backgroundColor: 'white',
                            borderRadius: '12px',
                            padding: '30px',
                            maxWidth: '500px',
                            width: '100%',
                            boxShadow: '0 10px 25px rgba(0,0,0,0.3)'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h2 style={{ marginTop: 0, color: '#333' }}>Print Map Preview</h2>
                        <p style={{ color: '#666', marginBottom: '24px' }}>
                            Configure your print options below. The map will be captured at its current view.
                        </p>

                        {/* Print Options */}
                        <div style={{ marginBottom: '24px' }}>
                            <label style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                padding: '12px',
                                backgroundColor: '#f9f9f9',
                                borderRadius: '6px',
                                marginBottom: '12px',
                                cursor: 'pointer'
                            }}>
                                <input
                                    type="checkbox"
                                    checked={printOptions.includeLegend}
                                    onChange={(e) => setPrintOptions({
                                        ...printOptions,
                                        includeLegend: e.target.checked
                                    })}
                                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                />
                                <span style={{ fontWeight: '500' }}>Include Legend</span>
                            </label>

                            <label style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                padding: '12px',
                                backgroundColor: '#f9f9f9',
                                borderRadius: '6px',
                                cursor: 'pointer'
                            }}>
                                <input
                                    type="checkbox"
                                    checked={printOptions.includeJobList}
                                    onChange={(e) => setPrintOptions({
                                        ...printOptions,
                                        includeJobList: e.target.checked
                                    })}
                                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                />
                                <span style={{ fontWeight: '500' }}>Include Job List</span>
                            </label>
                        </div>

                        {/* Print Info */}
                        <div style={{
                            padding: '15px',
                            backgroundColor: '#f0f9ff',
                            borderRadius: '6px',
                            marginBottom: '24px',
                            fontSize: '14px',
                            color: '#0369a1'
                        }}>
                            <p style={{ margin: 0 }}>
                                <strong>Page Size:</strong> A4 Landscape<br />
                                <strong>Active Skips:</strong> {activeSkips.length}<br />
                                <strong>Date:</strong> {new Date().toLocaleDateString()}
                            </p>
                        </div>

                        {/* Action Buttons */}
                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => setShowPrintPreview(false)}
                                style={{
                                    padding: '10px 20px',
                                    backgroundColor: '#f3f4f6',
                                    color: '#374151',
                                    border: 'none',
                                    borderRadius: '6px',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    cursor: 'pointer'
                                }}
                                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#e5e7eb'}
                                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handlePrint}
                                style={{
                                    padding: '8px 20px',
                                    backgroundColor: '#166534',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}
                                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#14532D'}
                                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#166534'}
                            >
                                Print
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Mobile Capability Warning */}
            {showMobileWarning && (
                <MobileCapabilityWarning onDismiss={() => setShowMobileWarning(false)} />
            )}

            {/* Mobile Bottom Sheet for Marker Details */}
            {viewport.isMobile && selectedMobileMarker && (
                <>
                    {/* Backdrop */}
                    <div
                        onClick={() => setSelectedMobileMarker(null)}
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            zIndex: 10000,
                            animation: 'fadeIn 0.3s ease-out'
                        }}
                    />

                    {/* Bottom Sheet */}
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            position: 'fixed',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            backgroundColor: 'white',
                            borderRadius: '16px 16px 0 0',
                            boxShadow: '0 -4px 24px rgba(0, 0, 0, 0.2)',
                            zIndex: 10001,
                            maxHeight: '80vh',
                            overflowY: 'auto',
                            animation: 'slideUp 0.3s ease-out',
                            padding: 'var(--space-lg)',
                            paddingBottom: 'calc(var(--space-lg) + env(safe-area-inset-bottom))'
                        }}
                    >
                        {/* Handle bar */}
                        <div style={{
                            width: '40px',
                            height: '4px',
                            backgroundColor: '#D1D5DB',
                            borderRadius: '2px',
                            margin: '0 auto 20px auto'
                        }} />

                        {/* Close button */}
                        <button
                            onClick={() => setSelectedMobileMarker(null)}
                            style={{
                                position: 'absolute',
                                top: '16px',
                                right: '16px',
                                backgroundColor: 'transparent',
                                border: 'none',
                                fontSize: '24px',
                                color: '#6B7280',
                                cursor: 'pointer',
                                padding: '8px',
                                minWidth: '44px',
                                minHeight: '44px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            ✕
                        </button>

                        {/* Image */}
                        {selectedMobileMarker.imageUrl && (
                            <div style={{
                                width: '100%',
                                marginBottom: '20px',
                                borderRadius: '8px',
                                overflow: 'hidden',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                            }}>
                                <img
                                    src={selectedMobileMarker.imageUrl}
                                    alt="Skip location"
                                    loading="lazy"
                                    style={{
                                        width: '100%',
                                        height: '200px',
                                        objectFit: 'cover'
                                    }}
                                    onClick={() => toggleEnlargedImage(selectedMobileMarker.imageUrl)}
                                    onError={(e) => {
                                        e.target.src = "https://via.placeholder.com/300x200?text=Image+Unavailable";
                                        e.target.style.opacity = 0.6;
                                    }}
                                />
                            </div>
                        )}

                        {/* Details */}
                        <div style={{ fontSize: '16px' }}>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                padding: '12px 0',
                                borderBottom: '1px solid #F3F4F6'
                            }}>
                                <span style={{ color: '#6B7280', fontWeight: '500' }}>Hire Days:</span>
                                <span style={{ fontWeight: '600', color: '#111827' }}>
                                    {(() => { const d = parseJobDate(selectedMobileMarker.initialTime, selectedMobileMarker.timestamp); return d ? Math.round((currentTime - d) / (1000 * 60 * 60 * 24)) : 'N/A'; })()} days
                                </span>
                            </div>

                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                padding: '12px 0',
                                borderBottom: '1px solid #F3F4F6'
                            }}>
                                <span style={{ color: '#6B7280', fontWeight: '500' }}>Waste Type:</span>
                                <span style={{ fontWeight: '600', color: '#111827' }}>{selectedMobileMarker.description}</span>
                            </div>

                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                padding: '12px 0',
                                borderBottom: '1px solid #F3F4F6'
                            }}>
                                <span style={{ color: '#6B7280', fontWeight: '500' }}>Skip Size:</span>
                                <span style={{ fontWeight: '600', color: '#111827' }}>{selectedMobileMarker.size}</span>
                            </div>

                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                padding: '12px 0',
                                borderBottom: '1px solid #F3F4F6'
                            }}>
                                <span style={{ color: '#6B7280', fontWeight: '500' }}>Delivery Date:</span>
                                <span style={{ fontWeight: '600', color: '#111827' }}>
                                    {(() => { const d = parseJobDate(selectedMobileMarker.initialTime, selectedMobileMarker.timestamp); return d ? d.toLocaleDateString() : 'N/A'; })()}
                                </span>
                            </div>

                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                padding: '12px 0',
                                borderBottom: '1px solid #F3F4F6'
                            }}>
                                <span style={{ color: '#6B7280', fontWeight: '500' }}>Driver:</span>
                                <span style={{ fontWeight: '600', color: '#111827' }}>{selectedMobileMarker.driverName}</span>
                            </div>

                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                padding: '12px 0',
                                borderBottom: '1px solid #F3F4F6',
                                alignItems: 'flex-start'
                            }}>
                                <span style={{ color: '#6B7280', fontWeight: '500' }}>Address:</span>
                                <span style={{ fontWeight: '600', color: '#111827', maxWidth: '60%', textAlign: 'right' }}>
                                    {selectedMobileMarker.streetAddress || "No address provided"}
                                </span>
                            </div>

                            {selectedMobileMarker.skipId && (
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    padding: '12px 0'
                                }}>
                                    <span style={{ color: '#6B7280', fontWeight: '500' }}>Skip ID:</span>
                                    <span style={{ fontWeight: '600', color: '#111827', fontSize: '14px' }}>
                                        {selectedMobileMarker.skipId}
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* View Details Button */}
                        <button
                            onClick={() => handleViewDetails(selectedMobileMarker.id)}
                            style={{
                                width: '100%',
                                padding: '12px 20px',
                                backgroundColor: '#166534',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: '14px',
                                fontWeight: '500',
                                cursor: 'pointer',
                                marginTop: '20px',
                                minHeight: '48px'
                            }}
                        >
                            View Full Details
                        </button>
                    </div>

                    <style>
                        {`
                            @keyframes fadeIn {
                                from {
                                    opacity: 0;
                                }
                                to {
                                    opacity: 1;
                                }
                            }

                            @keyframes slideUp {
                                from {
                                    transform: translateY(100%);
                                }
                                to {
                                    transform: translateY(0);
                                }
                            }
                        `}
                    </style>
                </>
            )}
        </div>
    );
};

export default MapComponent;
